import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { apiRequest } from '../utils/api';

interface FileComment {
  id: number;
  fileId: number;
  pageNumber: number;
  xPosition: number;
  yPosition: number;
  commentText: string;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
  creatorName?: string | null;
}

interface CommentDialogProps {
  open: boolean;
  onClose: () => void;
  comment: FileComment | null;
  jobId: number;
  fileId: number;
  onUpdate: () => void;
  onDelete: () => void;
  currentUserId?: number;
}

export default function CommentDialog({
  open,
  onClose,
  comment,
  jobId,
  fileId,
  onUpdate,
  onDelete,
  currentUserId,
}: CommentDialogProps) {
  const [commentText, setCommentText] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (comment) {
      setCommentText(comment.commentText);
      setIsEditing(false);
    }
  }, [comment]);

  const handleSave = async () => {
    if (!comment || !commentText.trim()) return;

    setSaving(true);
    try {
      await apiRequest(
        `/jobs/${jobId}/files/${fileId}/comments/${comment.id}`,
        {
          method: 'PUT',
          body: JSON.stringify({ commentText: commentText.trim() }),
        }
      );
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      console.error('Error updating comment:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!comment) return;

    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    setDeleting(true);
    try {
      await apiRequest(
        `/jobs/${jobId}/files/${fileId}/comments/${comment.id}`,
        { method: 'DELETE' }
      );
      onDelete();
      onClose();
    } catch (error) {
      console.error('Error deleting comment:', error);
    } finally {
      setDeleting(false);
    }
  };

  const canEdit = comment && currentUserId && comment.createdBy === currentUserId;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box component="span">Comment</Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {comment && (
            <>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Chip label={`Page ${comment.pageNumber}`} size="small" />
                {comment.creatorName && (
                  <Chip label={`By ${comment.creatorName}`} size="small" variant="outlined" />
                )}
                <Typography variant="caption" color="text.secondary">
                  {new Date(comment.createdAt).toLocaleString()}
                </Typography>
              </Box>

              {isEditing ? (
                <TextField
                  multiline
                  rows={4}
                  fullWidth
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Enter your comment..."
                  autoFocus
                />
              ) : (
                <Box
                  sx={(theme) => ({
                    p: 2,
                    bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
                    borderRadius: 1,
                    minHeight: '100px',
                  })}
                >
                  <Typography variant="body1" color="text.primary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {comment.commentText}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {canEdit && (
          <>
            {isEditing ? (
              <>
                <Button onClick={() => setIsEditing(false)} disabled={saving}>
                  Cancel
                </Button>
                <Button onClick={handleSave} variant="contained" disabled={saving || !commentText.trim()}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </>
            ) : (
              <>
                <Button
                  startIcon={<DeleteIcon />}
                  onClick={handleDelete}
                  color="error"
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </Button>
                <Button
                  startIcon={<EditIcon />}
                  onClick={() => setIsEditing(true)}
                  variant="contained"
                >
                  Edit
                </Button>
              </>
            )}
          </>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
